import {
  createSlice,
  createAsyncThunk,
  createEntityAdapter,
} from '@reduxjs/toolkit';
import { normalize, schema } from 'normalizr';
import fetchAllContacts from 'network/fetchAllContacts';

export const contactEntity = new schema.Entity('contacts');

export const fetchContactList = createAsyncThunk(
  'contacts/fetchAll',
  async (userId: string, { getState }: { getState: any }) => {
    const { lastCursor } = getState().contacts;

    try {
      const {
        pageInfo: {
          endCursor,
          hasNextPage,
        },
        edges,
      } = await fetchAllContacts(userId, '', lastCursor);
      const contacts = edges.filter(({ node }) => !!node.contact).map(({ cursor, node }) => {
        const id = node.contact.uuid;
        return {
          ...node,
          id,
          cursor,
        };
      });
      const normalized = normalize(contacts, [contactEntity]);
      return {
        entities: normalized.entities,
        lastCursor: endCursor,
        hasNextPage,
      };
    } catch (error) {
      console.error(error);
    }
    return {
      entities: {},
    };
  },
  {
    condition: (_, { getState }: { getState: any}) => {
      const { loading } = getState().contacts;
      return !loading;
    },
  },
);

const contactsAdapter = createEntityAdapter();

export const {
  selectById: selectContactsById,
  selectIds: selectContactIds,
  selectEntities: selectContactEntities,
  selectAll: selectAllContacts,
  selectTotal: selectTotalContacts,
} = contactsAdapter.getSelectors((state: any) => state.contacts);

const initialState = {
  ...contactsAdapter.getInitialState(),
  lastCursor: undefined,
  initialLoading: true,
  hasNextPage: true,
};

export const ContactsSlice = createSlice({
  name: 'contacts',
  initialState,
  reducers: {
    addContactToRedux(state, action) {
      const contact = action.payload;
      const normalizedData = normalize(contact, contactEntity);
      contactsAdapter.upsertMany(state, normalizedData.entities.contacts);
    },
    resetContacts() {
      return initialState;
    },
    removeContactById(state, action) {
      contactsAdapter.removeOne(state, action.payload);
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchContactList.pending, (state: any) => {
      state.loading = true;
    });
    builder.addCase(fetchContactList.fulfilled, (state: any, action: any) => {
      const results = action.payload.entities.contacts || {};
      contactsAdapter.upsertMany(state, results);
      state.lastCursor = action.payload.lastCursor;
      state.hasNextPage = action.payload.hasNextPage;
      state.initialLoading = false;
      state.loading = false;
    });
  },
});

export const {
  removeContactById,
  addContactToRedux,
} = ContactsSlice.actions;

export default ContactsSlice.reducer;
